<template>
    <div>
        <h2
            v-t="'Direkte Abteilungszugehörigkeiten'"
            class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText"
        ></h2>

        <kgr-checkbox
            v-model="showOnlyActive"
            :label="$t('nur aktuelle Daten anzeigen')"
            class="kgr-row"
            @change="handleCheckbox()"
        />

        <kgr-table
            class="kgr-table--block"
            :searching="false"
        >
            <thead>
                <tr class="kgr-table__row">
                    <kgr-th sortable>
                        {{ $t('employee') }}
                    </kgr-th>
                    <kgr-th sortable>
                        {{ $t('Start') }}
                    </kgr-th>
                    <kgr-th sortable>
                        {{ $t('Ende') }}
                    </kgr-th>
                    <kgr-th :sortable="false" />
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="departmentRelation in visibleRelations"
                    :key="departmentRelation.id"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <span v-if="departmentRelation.employee.isShortProfileViewable === false">
                            {{ departmentRelation.employee.fullName }}
                        </span>
                        <a
                            v-else
                            class="kgr-link kgr-link--unobtrusive"
                            :href="getLink(departmentRelation.employee.id)"
                        >{{ departmentRelation.employee.fullName }}</a>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(departmentRelation)">
                            <div class="form-widget without-label">
                                <kgr-date-picker
                                    v-model="editRelations[getEditIndex(departmentRelation)].start"
                                    :invalid="getErrorMessageForExistingStartDate(editRelations[getEditIndex(departmentRelation)]) !== null || isErrorRelation(editRelations[getEditIndex(departmentRelation)])"
                                />
                            </div>
                            <div v-if="getErrorMessageForExistingStartDate(editRelations[getEditIndex(departmentRelation)]) !== null || isErrorRelation(editRelations[getEditIndex(departmentRelation)])">
                                {{ getErrorMessageForExistingStartDate(editRelations[getEditIndex(departmentRelation)]) }}
                            </div>
                        </template>
                        <template v-else>
                            {{ departmentRelation.formattedStart() }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(departmentRelation)">
                            <div class="form-widget without-label">
                                <kgr-date-picker
                                    v-model="editRelations[getEditIndex(departmentRelation)].end"
                                    :invalid="getErrorMessageForExistingEndDate(editRelations[getEditIndex(departmentRelation)]) !== null || isErrorRelation(editRelations[getEditIndex(departmentRelation)])"
                                />
                            </div>
                            <div v-if="getErrorMessageForExistingEndDate(editRelations[getEditIndex(departmentRelation)]) !== null || isErrorRelation(editRelations[getEditIndex(departmentRelation)])">
                                {{ getErrorMessageForExistingEndDate(editRelations[getEditIndex(departmentRelation)]) }}
                            </div>
                        </template>
                        <template v-else>
                            {{ departmentRelation.formattedEnd() }}
                        </template>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <kgr-icon-edit
                            v-if="departmentRelation.isEditable"
                            :size="15"
                            :disabled="isBeingEdited(departmentRelation)"
                            class="kgr-table__action"
                            @click="edit(departmentRelation)"
                        />
                        <kgr-icon-delete
                            v-if="departmentRelation.isDeletable"
                            class="kgr-table__action"
                            @click="remove(departmentRelation)"
                        />
                    </td>
                </tr>
            </tbody>
            <tfoot>
                <tr
                    v-if="isCreatable"
                    class="kgr-table__row kgr-table__row--top"
                >
                    <td class="kgr-table__cell">
                        <kgr-club-employee-select
                            ref="clubEmployeeSelect"
                            v-model="newRelation.employee"
                            :club-id="clubId"
                            hide-label
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label date-picker">
                            <kgr-date-picker
                                v-model="newRelation.start"
                                :invalid="getErrorMessageForNewRelationStartDate(newRelation) !== null || newRelationIsInvalid"
                            />
                        </div>
                        <div v-if="getErrorMessageForNewRelationStartDate(newRelation) !== null || newRelationIsInvalid">
                            {{ getErrorMessageForNewRelationStartDate(newRelation) }}
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label date-picker">
                            <kgr-date-picker
                                v-model="newRelation.end"
                                :invalid="getErrorMessageForNewRelationEndDate(newRelation) !== null || newRelationIsInvalid"
                            />
                        </div>
                        <div v-if="getErrorMessageForNewRelationEndDate(newRelation) !== null || newRelationIsInvalid">
                            {{ getErrorMessageForNewRelationEndDate(newRelation) }}
                        </div>
                    </td>
                    <td class="kgr-table__cell">
&nbsp;
                    </td>
                </tr>
            </tfoot>
        </kgr-table>

        <kgr-button
            v-if="showSaveButton"
            class="button_save"
            :value="$t('saveChanges')"
            :disabled="!isSavable()"
            :pending="pendingCreate || pendingUpdate"
            @click="save()"
        />
    </div>
</template>

<script>
import Checkbox from '../common/form/checkbox/Checkbox';
import ClubEmployeeDepartmentRelation from '../../js/dto/ClubEmployeeDepartmentRelation';
import ClubEmployeeRepository from '../../js/repository/ClubEmployeeRepository';
import ClubEmployeesSelect from '../common/form/select/ClubEmployeesSelect';
import DatePicker from '../common/form/picker/DatePicker';
import Error from '../../js/model/Error';
import IconDelete from '../common/icon/IconDelete';
import IconEdit from '../common/icon/IconEdit';
import KgrButton from '../common/form/button/KgrButton';
import axios from 'axios';

const DEPARTMENT_EMPLOYEE_RELATION_SHOW_ONLY_ACTIVE = 'kgr:department:employee:relation:show_only_active';

export default {
    name: 'KgrDepartmentEmployeeRelationsForm',
    components: {
        'kgr-club-employee-select': ClubEmployeesSelect,
        'kgr-icon-edit': IconEdit,
        'kgr-icon-delete': IconDelete,
        'kgr-button': KgrButton,
        'kgr-date-picker': DatePicker,
        'kgr-checkbox': Checkbox,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        departmentId: {
            type: Number,
            required: true,
        },
        departmentEmployeeRelations: {
            type: Array,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            newRelation: new ClubEmployeeDepartmentRelation(),
            editRelations: [],
            pendingCreate: false,
            pendingUpdate: false,
            showOnlyActive: false,
            alertMessages: [],
            newRelationIsInvalid: false,
        }
    },
    computed: {
        visibleRelations() {
            return this.departmentEmployeeRelations
                .filter(relation => this.isActive(relation));
        },
        showSaveButton() {
            return this.isCreatable ||
                this.departmentEmployeeRelations.filter(r => r.isEditable).length > 0 ||
                this.departmentEmployeeRelations.filter(r => r.isDeletable).length > 0;
        },
    },
    created() {
        let viewOnlyActive = localStorage.getItem(DEPARTMENT_EMPLOYEE_RELATION_SHOW_ONLY_ACTIVE);
        if (null !== viewOnlyActive && '' !== viewOnlyActive) {
            this.showOnlyActive = true;
        }
    },
    methods: {
        isErrorRelation(relation) {
            return relation !== undefined && relation.invalid;
        },
        getLink(employeeId) {
            return `/backend/clubs/employees/${employeeId}/show`;
        },
        handleCheckbox() {
            if (this.showOnlyActive) {
                localStorage.setItem(DEPARTMENT_EMPLOYEE_RELATION_SHOW_ONLY_ACTIVE, 'true');
            } else {
                localStorage.removeItem(DEPARTMENT_EMPLOYEE_RELATION_SHOW_ONLY_ACTIVE);
            }
        },
        isActive(departmentRelation) {
            if (this.showOnlyActive && !this.isRelationCurrent(departmentRelation)) {
                return false;
            }

            return true;
        },
        isRelationCurrent(departmentRelation) {
            let today = new Date();
            today.setHours(0,today.getTimezoneOffset() * -1, 0, 0);
            if (!departmentRelation.end) {
                if (departmentRelation.start <= today) {
                    return true;
                }
            } else {
                if (departmentRelation.start <= today && departmentRelation.end >= today) {
                    return true;
                }
            }

            return false;
        },
        isBeingEdited(departmentRelation) {
            return this.getEditIndex(departmentRelation) !== -1;
        },
        getEditIndex(departmentRelation) {
            return this.editRelations.map(editRelation => editRelation.id).indexOf(departmentRelation.id);
        },
        isSavable() {
            if (this.editRelations.length === 0 && this.newRelation.isEmptyEmployee()) {
                return false;
            }

            let hasInvalidEdits = this.editRelations.filter(editRelation => !this.isValidDateRange(editRelation)).length > 0;
            if (hasInvalidEdits) {
                return false;
            }
            if (!this.newRelation.isEmptyEmployee() && !this.newRelation.isValidEmployee()) {
                return false;
            }

            return true;
        },
        isValidDateRange(departmentRelation) {
            if (!departmentRelation.end) {
                return true;
            }
            if (!departmentRelation.start && departmentRelation.end) {
                return false;
            }

            return this.getDateFromString(departmentRelation.end) >=  this.getDateFromString(departmentRelation.start);
        },
        getDateFromString(dateString) {
            let pattern = /(\d{2})\.(\d{2})\.(\d{4})/;

            return new Date(dateString.replace(pattern,'$3-$2-$1'));
        },
        getErrorMessageForExistingStartDate(relation) {
            let editRelation = this.editRelations[this.getEditIndex(relation)];
            if (editRelation.invalid === true) {
                return this.$t('relation.error.overlapping');
            }
            if (!relation.start) {
                return this.$t('Das Startdatum darf nicht leer sein.');
            }
            if (!this.isValidDate(relation.start)) {
                return this.$t('Das Datum muss im Format DD.MM.YYYY sein.');
            }
            if (!this.isValidDateRange(relation)) {
                return this.$t('Das Startdatum darf nicht nach dem Enddatum liegen.');
            }
            return null;
        },
        getErrorMessageForExistingEndDate(relation) {
            if (relation.end && !this.isValidDate(relation.end)) {
                return this.$t('Das Datum muss im Format DD.MM.YYYY sein.');
            }
            if (relation.start && !this.isValidDateRange(relation)) {
                return this.$t('Das Enddatum darf nicht vor dem Startdatum liegen.');
            }
            return null;
        },
        getErrorMessageForNewRelationStartDate(newRelation) {
            if (this.newRelationIsInvalid) {
                return this.$t('{name} ist dieser Abteilung im gewählten Zeitraum bereits zugewiesen.', {'name': this.newRelation.employee.fullName});
            }
            if (!newRelation.isEmptyEmployee() && !newRelation.start) {
                return this.$t('Das Startdatum darf nicht leer sein.');
            }
            if (!newRelation.isEmptyEmployee() && !this.isValidDate(newRelation.start)) {
                return this.$t('Das Datum muss im Format DD.MM.YYYY sein.');
            }
            if (!this.isValidDateRange(newRelation)) {
                return this.$t('Das Startdatum darf nicht nach dem Enddatum liegen.');
            }
            return null;
        },
        getErrorMessageForNewRelationEndDate(newRelation) {
            if (newRelation.end && !this.isValidDate(newRelation.end)) {
                return this.$t('Das Datum muss im Format DD.MM.YYYY sein.');
            }
            if (!this.isValidDateRange(newRelation)) {
                return this.$t('Das Enddatum darf nicht vor dem Startdatum liegen.');
            }
            return null;
        },
        isValidDate(date) {
            return this.getRegexForDate().test(date);
        },
        getRegexForDate() {
            return new RegExp('^([0-2][0-9]|(3)[0-1])(\\.)(((0)[0-9])|((1)[0-2]))(\\.)\\d{4}$');
        },
        remove(departmentRelation) {
            showConfirm(this.$t('confirm'), this.$t('department.relation.employee.confirm.delete', {'name': departmentRelation.employee.fullName}), () => {
                this.editRelations = this.editRelations.filter(r => r.id !== departmentRelation.id);
                ClubEmployeeRepository
                    .removeEmployeeDepartmentRelation(departmentRelation.employee.id, departmentRelation.id)
                    .then(() => {
                        this.$notify.success('department.relation.employee.delete.success', {'name': departmentRelation.employee.fullName});
                        this.emitUpdated();
                    })
                    .catch(() => {
                        this.$notify.error('saveChanges.error');
                    });
            }, 'Ok');
        },
        edit(departmentRelation) {
            if (this.isBeingEdited(departmentRelation)) {
                return;
            }

            this.editRelations.push(departmentRelation.toData());
        },
        save() {
            this.pendingCreate = true;
            this.pendingUpdate = true;

            let apiCalls = [];
            if (!this.newRelation.isEmptyEmployee()) {
                this.newRelationIsInvalid = false;
                apiCalls.push(this._createRelation());
            }
            if (this.editRelations.length > 0) {
                apiCalls.push(this._updateRelations(this.editRelations));
            }
            if (apiCalls.length === 0) {
                return;
            }

            axios.all(apiCalls)
                .then(() => {
                    if (this.alertMessages.length > 0 || this.newRelationIsInvalid) {
                        this.alertMessages = this.alertMessages.filter((value, index) => this.alertMessages.indexOf(value) === index);
                        this.$notify.error(this.alertMessages);
                    } else {
                        this.$notify.success('saveChanges.success');
                    }
                })
                .finally(() => {
                    this.editRelations = this.editRelations.filter(relation => relation.invalid === true);
                    this.emitUpdated();
                    this.pendingCreate = false;
                    this.pendingUpdate = false;
                    this.alertMessages = [];
                });

        },
        _createRelation() {
            this.newRelationIsInvalid = false;
            return ClubEmployeeRepository
                .createDepartmentRelation(this.departmentId, this.newRelation, this.newRelation.employee.id)
                .then(() => {
                    this.newRelation = new ClubEmployeeDepartmentRelation();
                    this.newRelationIsInvalid = false;
                    this.$refs.clubEmployeeSelect.resetSelected();
                })
                .catch(error => {
                    this.newRelationIsInvalid = true;
                    if (error.status === 409) {
                        this.alertMessages.push(this.$t('{name} ist dieser Abteilung im gewählten Zeitraum bereits zugewiesen.', {'name': this.newRelation.employee.fullName}));
                    } else {
                        this.alertMessages.push(this.$t('Änderungen konnten nicht gespeichert werden. Bitte überprüfe die markierten Einträge.'));
                    }
                })
        },
        _updateRelations(editRelations) {
            return axios.all(editRelations.map(departmentRelation => {
                departmentRelation.invalid = false;
                return ClubEmployeeRepository
                    .updateDepartmentRelation(departmentRelation)
                    .catch(() => {
                        this.alertMessages.push(this.$t('Änderungen konnten nicht gespeichert werden. Bitte überprüfe die markierten Einträge.'));
                        departmentRelation.invalid = true;
                    })
            }));
        },
        emitUpdated() {
            this.$emit('updated');
        }
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
.button_save {
    display: block;
    margin-left: auto;
}

.hierarchyPath {
    font-size: 80%;
}
</style>
